import { useState, useEffect, useMemo } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import PropertyMap from "@/components/PropertyMap";

/**
 * PropertyMediaGallery
 * Swipeable Zillow-style hero for a property. Slides:
 *   1. Satellite close-up (zoom 20)
 *   2. Satellite wide (zoom 17)
 *   3. Street map (zoom 17)
 *   4. Google Street View — opens in a new tab (link card, no API key)
 *   5+ User-uploaded photos (base64 or URL)
 *
 * All layers work WITHOUT a Google Maps API key.
 */
export default function PropertyMediaGallery({
  address,
  photos = [], // string[] — data URLs or public URLs
  height = 288,
}) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: "start" });
  const [selectedIdx, setSelectedIdx] = useState(0);

  const slides = useMemo(() => {
    if (!address) return [];
    // Front-of-house Street View leads ONLY when a Google Maps API key is
    // wired up (proxy or client-side). Without a key, the Street View slide
    // is just a CTA card — bad first impression — so we push it to the end
    // and lead with the aerial-close satellite view (real roof shot of the
    // house, works free-forever with our OSM tile provider). Add
    // REACT_APP_GOOGLE_MAPS_API_KEY or GOOGLE_MAPS_API_KEY on the server to
    // promote Street View back to the top.
    const hasGoogleKey =
      Boolean(process.env.REACT_APP_GOOGLE_MAPS_API_KEY) ||
      Boolean(process.env.REACT_APP_HAS_STREETVIEW_PROXY);   // set by build if backend has key
    const streetView = { key: "streetview", label: "Front of house · Street View", type: "streetview" };
    const aerialClose = { key: "sat-close", label: "Aerial · close-up", type: "map",
      props: { initialView: "satellite", zoomOverride: 20 } };
    const aerialWide = { key: "sat-wide", label: "Aerial · wide", type: "map",
      props: { initialView: "satellite", zoomOverride: 17 } };
    const streetMap = { key: "map", label: "Street map", type: "map",
      props: { initialView: "map", zoomOverride: 17 } };
    const base = hasGoogleKey
      ? [streetView, aerialClose, aerialWide, streetMap]
      : [aerialClose, aerialWide, streetMap, streetView];
    const userSlides = (photos || []).map((p, i) => ({
      key: `photo-${i}`,
      label: i === 0 ? "Front of house" : `Photo ${i + 1}`,
      type: "photo",
      src: p,
    }));
    // User/Zillow photos ALWAYS lead when available — that's the front-of-
    // house shot reps actually want to see first. Map/satellite slides come
    // next as contextual views.
    return [...userSlides, ...base];
  }, [address, photos]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIdx(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => { emblaApi.off("select", onSelect); };
  }, [emblaApi]);

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();
  const scrollTo = (i) => emblaApi?.scrollTo(i);

  if (!address || slides.length === 0) {
    return (
      <div
        style={{ height }}
        className="flex items-center justify-center text-xs text-muted-foreground bg-secondary/40"
        data-testid="property-media-empty"
      >
        Enter an address to see property media
      </div>
    );
  }

  return (
    <div
      className="relative"
      style={{ height }}
      data-testid="property-media-gallery"
    >
      <div className="overflow-hidden h-full" ref={emblaRef}>
        <div className="flex h-full">
          {slides.map((s, i) => (
            <div
              key={s.key}
              className="min-w-0 flex-[0_0_100%] h-full relative"
              data-testid={`property-media-slide-${s.key}`}
            >
              {s.type === "map" && (
                <PropertyMap
                  address={address}
                  height="100%"
                  initialView={s.props.initialView}
                  zoomOverride={s.props.zoomOverride}
                  hideToggle
                  hideStreetViewLink
                />
              )}
              {s.type === "streetview" && (
                <StreetViewSlide address={address} />
              )}
              {s.type === "photo" && (
                <div className="w-full h-full bg-black flex items-center justify-center">
                  <img
                    src={s.src}
                    alt={s.label}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              {/* Slide label — top-left */}
              <div
                className="absolute top-2 left-2 z-[400] text-[10px] uppercase tracking-widest bg-white/95 backdrop-blur border border-[var(--app-line)] rounded-full px-2.5 py-1 shadow-sm text-slate-700"
                data-testid={`property-media-label-${s.key}`}
              >
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Prev / Next arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={scrollPrev}
            disabled={selectedIdx === 0}
            data-testid="property-media-prev"
            className="absolute left-2 top-1/2 -translate-y-1/2 z-[500] w-9 h-9 rounded-full bg-white/95 backdrop-blur border border-[var(--app-line)] shadow-sm flex items-center justify-center hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            aria-label="Previous photo"
          >
            <ChevronLeft size={18} strokeWidth={2.2} />
          </button>
          <button
            onClick={scrollNext}
            disabled={selectedIdx >= slides.length - 1}
            data-testid="property-media-next"
            className="absolute right-2 top-1/2 -translate-y-1/2 z-[500] w-9 h-9 rounded-full bg-white/95 backdrop-blur border border-[var(--app-line)] shadow-sm flex items-center justify-center hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            aria-label="Next photo"
          >
            <ChevronRight size={18} strokeWidth={2.2} />
          </button>

          {/* Dots — bottom-center */}
          <div
            className="absolute bottom-2 left-1/2 -translate-x-1/2 z-[500] flex items-center gap-1.5 bg-white/95 backdrop-blur border border-[var(--app-line)] rounded-full px-2.5 py-1.5 shadow-sm"
            data-testid="property-media-dots"
          >
            {slides.map((s, i) => (
              <button
                key={s.key}
                onClick={() => scrollTo(i)}
                data-testid={`property-media-dot-${i}`}
                aria-label={`Go to ${s.label}`}
                className={`transition-all rounded-full ${
                  selectedIdx === i
                    ? "w-4 h-1.5 bg-slate-900"
                    : "w-1.5 h-1.5 bg-slate-300 hover:bg-slate-500"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * StreetViewSlide — shows the front of the house.
 *
 * If REACT_APP_GOOGLE_MAPS_API_KEY is set, we embed the Google Maps
 * Static Street View API directly (renders the actual front-of-house
 * image inline). Otherwise, we fall back to a beautiful CTA card that
 * opens Google Street View in a new tab (no API key required).
 *
 * How to add the key (5 min, free tier = $200/mo credit):
 *   1. Google Cloud Console → APIs & Services → Enable "Street View Static API"
 *   2. Credentials → Create API key
 *   3. Add REACT_APP_GOOGLE_MAPS_API_KEY=<key> to your production env
 *   4. Redeploy
 */
const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function StreetViewSlide({ address }) {
  const [coords, setCoords] = useState(null);
  const [imgError, setImgError] = useState(false);
  // Prefer the backend proxy first — the key only needs to be set once by
  // an admin and every rep sees the front of the house. If the proxy 404s
  // (no key configured / no imagery), we fall through to the direct Google
  // URL (works only if REACT_APP_GOOGLE_MAPS_API_KEY is set) and finally to
  // the "Open in Google Maps" CTA.
  const [proxyFailed, setProxyFailed] = useState(false);

  useEffect(() => {
    if (!address) return;
    fetch(
      "https://nominatim.openstreetmap.org/search?format=json&limit=1&q=" +
        encodeURIComponent(address),
      { headers: { Accept: "application/json" } }
    )
      .then((r) => r.json())
      .then((rows) => {
        if (rows && rows.length) {
          setCoords([parseFloat(rows[0].lat), parseFloat(rows[0].lon)]);
        }
      })
      .catch(() => {});
  }, [address]);

  const openUrl = coords
    ? `https://www.google.com/maps?q=&layer=c&cbll=${coords[0]},${coords[1]}`
    : `https://www.google.com/maps?q=${encodeURIComponent(address)}&layer=c`;

  // ---- Preferred path: BACKEND PROXY ----
  // Uses GOOGLE_MAPS_API_KEY on the server or app_settings row. Zero client
  // config needed. `credentials: "include"` because the endpoint is auth-
  // gated — <img> tag automatically sends the browser's cookies + Authorization
  // header via our axios wrapper isn't possible on <img>, so we rely on the
  // JWT cookie. Falls through to next path on error.
  if (!proxyFailed && BACKEND_URL) {
    const proxyUrl =
      `${BACKEND_URL}/api/property/streetview?address=${encodeURIComponent(address)}`;
    return (
      <div className="relative w-full h-full bg-black">
        <img
          src={proxyUrl}
          alt={`Front of ${address}`}
          className="w-full h-full object-cover"
          onError={() => setProxyFailed(true)}
          crossOrigin="anonymous"
          data-testid="property-media-streetview-proxy"
        />
        <a
          href={openUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute bottom-2 right-2 z-[400] inline-flex items-center gap-1 bg-white/95 backdrop-blur border border-[var(--app-line)] rounded-full px-2.5 py-1 shadow-sm text-[10px] uppercase tracking-widest text-slate-700 hover:bg-slate-100"
          title="Open Street View in a new tab"
        >
          Look around
          <ExternalLink size={10} strokeWidth={2.4} className="opacity-70" />
        </a>
      </div>
    );
  }

  // ---- KEY PATH: real front-of-house image via Static Street View API ----
  if (GOOGLE_KEY && !imgError) {
    // The Static Street View API auto-picks the panorama nearest to the
    // address and returns a 640×640 JPEG facing the building.
    const staticUrl =
      `https://maps.googleapis.com/maps/api/streetview` +
      `?size=640x480&location=${encodeURIComponent(address)}` +
      `&fov=80&pitch=0&key=${GOOGLE_KEY}`;
    return (
      <div className="relative w-full h-full bg-black">
        <img
          src={staticUrl}
          alt={`Front of ${address}`}
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
          data-testid="property-media-streetview-static"
        />
        <a
          href={openUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute bottom-2 right-2 z-[400] inline-flex items-center gap-1 bg-white/95 backdrop-blur border border-[var(--app-line)] rounded-full px-2.5 py-1 shadow-sm text-[10px] uppercase tracking-widest text-slate-700 hover:bg-slate-100"
          title="Open Street View in a new tab"
        >
          Look around
          <ExternalLink size={10} strokeWidth={2.4} className="opacity-70" />
        </a>
      </div>
    );
  }

  // ---- FALLBACK PATH: no API key → beautiful CTA card opens Street View ----
  return (
    <a
      href={openUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="property-media-streetview-slide"
      className="w-full h-full flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-slate-100 to-slate-200 hover:from-slate-200 hover:to-slate-300 transition-colors group"
    >
      <div className="w-16 h-16 rounded-full bg-slate-900 text-white flex items-center justify-center group-hover:scale-110 transition-transform">
        <ExternalLink size={26} strokeWidth={2.4} />
      </div>
      <div className="text-center px-6">
        <div className="font-display text-lg font-bold text-slate-900">
          Open Google Street View
        </div>
        <div className="text-xs text-slate-600 mt-1 max-w-xs">
          Click to see the front of the house — drag to look around
        </div>
        <div className="text-[10px] text-slate-500 mt-2 max-w-xs">
          💡 Add a Google Maps API key to your env to render this photo inline
        </div>
      </div>
    </a>
  );
}
